import React, { useCallback, useEffect, useState } from 'react';
import { Alert, useMediaQuery, useTheme } from '@mui/material';
import { deleteDoc, doc } from 'firebase/firestore';
import { useMeetingNotesData } from '../../hooks/useMeetingNotesData';
import { useCaseAccess } from '../../hooks/useCaseAccess';
import { useSessionQuestions } from '../../contexts/SessionQuestionsContext';
import { db } from '../../firebase';
import { MeetingNote } from '../../types';
import ConfirmDialog from '../common/ConfirmDialog';
import MeetingNotesToolbar from './MeetingNotesToolbar';
import MeetingNotesCaseList from './MeetingNotesCaseList';
import MeetingNotesSessionRail from './MeetingNotesSessionRail';
import MeetingNoteFormDialog from './MeetingNoteFormDialog';
import { ContentTypeFilter } from './meetingNotesUtils';
import { t } from '../../utils/translations';

const MeetingNotesSkeleton: React.FC = () => (
  <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,340px)_1fr]">
    <div className="h-[28rem] animate-pulse rounded-xl bg-slate-100" />
    <div className="h-[28rem] animate-pulse rounded-xl bg-slate-100" />
  </div>
);

const MeetingNotesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true });
  const data = useMeetingNotesData();
  const access = useCaseAccess();
  const { openTablou } = useSessionQuestions();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<MeetingNote | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MeetingNote | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [contentTypeFilter, setContentTypeFilter] = useState<ContentTypeFilter>('all');

  const openAdd = useCallback(() => {
    if (!data.selectedSummary) return;
    setEditingNote(null);
    setFormOpen(true);
  }, [data.selectedSummary]);

  /** Only meaningful for one concrete session — "Fără sesiune" has no tablou of its own. */
  const selectedSessionNumber =
    typeof data.selectedSessionFilter === 'number' ? data.selectedSessionFilter : null;

  const hasTablouForSelectedSession =
    selectedSessionNumber != null &&
    (data.selectedSummary?.notes.some(
      (note) => note.sessionNumber === selectedSessionNumber && note.tablou
    ) ??
      false);

  const openAddTablou = useCallback(() => {
    if (!data.selectedSummary || selectedSessionNumber == null) return;
    openTablou({ caseId: data.selectedSummary.case.id, sessionNumber: selectedSessionNumber });
  }, [data.selectedSummary, selectedSessionNumber, openTablou]);

  const openEdit = useCallback((note: MeetingNote) => {
    setEditingNote(note);
    setFormOpen(true);
  }, []);

  /**
   * A tablou reopens in its own editor, for whoever can see the case — the apprentice included,
   * since on a shadowed case they work the same tablou as their mentor.
   */
  const openTabloulNote = useCallback(
    (note: MeetingNote) => {
      if (note.sessionNumber == null) return;
      openTablou({ caseId: note.caseId, sessionNumber: note.sessionNumber });
    },
    [openTablou]
  );

  const handleSelectCase = useCallback(
    (caseId: string) => {
      data.selectCase(caseId);
      setMobileOpen(true);
    },
    [data]
  );

  // A content-type choice belongs to the case it was made on — carrying "Tablou" over to a case
  // that has none would silently show an empty list with no obvious reason why.
  useEffect(() => {
    setContentTypeFilter('all');
  }, [data.selectedCaseId]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      await deleteDoc(doc(db, 'meetingNotes', deleteTarget.id));
      setDeleteTarget(null);
      await data.refetch();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (data.loading) {
    return (
      <div>
        <MeetingNotesSkeleton />
      </div>
    );
  }

  const selectedCaseId = data.selectedSummary?.case.id ?? '';
  const railProps = {
    summary: data.selectedSummary,
    sessionFilters: data.sessionRailFilters,
    selectedFilter: data.selectedSessionFilter,
    onSelectFilter: data.setSelectedSessionFilter,
    contentTypeFilter,
    onSelectContentTypeFilter: setContentTypeFilter,
    onAddNote:
      data.selectedSummary && access.canAddNote(selectedCaseId) ? openAdd : undefined,
    onAddTablou:
      data.selectedSummary &&
      access.canAddNote(selectedCaseId) &&
      selectedSessionNumber != null &&
      !hasTablouForSelectedSession
        ? openAddTablou
        : undefined,
    onEditNote: openEdit,
    onOpenTablou: openTabloulNote,
    onDeleteNote: (note: MeetingNote) => setDeleteTarget(note),
    canModifyNote: (note: MeetingNote) => access.canModifyNote(selectedCaseId, note),
    isNoteByApprentice: (note: MeetingNote) =>
      access.isNoteByApprentice(selectedCaseId, note),
    readOnlyNotice: access.isShadowedReadOnly(selectedCaseId)
      ? t.meetingNotes.mentorshipEndedReadOnly
      : undefined,
  };

  return (
    <div>
      {data.error && (
        <Alert severity="error" sx={{ mb: 3 }} className="rounded-xl">
          {data.error}
        </Alert>
      )}

      <MeetingNotesToolbar
        searchTerm={data.searchTerm}
        onSearchChange={data.setSearchTerm}
        statusFilter={data.statusFilter}
        onStatusFilterChange={data.setStatusFilter}
        filteredCount={data.filteredSummaries.length}
      />

      <div
        className={
          isMobile
            ? ''
            : 'grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,340px)_1fr] lg:items-start'
        }
      >
        <MeetingNotesCaseList
          summaries={data.filteredSummaries}
          selectedCaseId={data.selectedCaseId}
          onSelectCase={handleSelectCase}
        />
        {!isMobile && <MeetingNotesSessionRail variant="panel" {...railProps} />}
      </div>

      {isMobile && (
        <MeetingNotesSessionRail
          variant="drawer"
          open={mobileOpen && Boolean(data.selectedSummary)}
          onClose={() => setMobileOpen(false)}
          {...railProps}
        />
      )}

      <MeetingNoteFormDialog
        open={formOpen}
        caseItem={data.selectedSummary?.case ?? null}
        reportCount={data.reportCountForSelected}
        editingNote={editingNote}
        onClose={() => {
          setFormOpen(false);
          setEditingNote(null);
        }}
        onSaved={() => {
          void data.refetch();
        }}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title={t.meetingNotes.deleteNote}
        message={t.meetingNotes.deleteNoteConfirm}
        variant="danger"
        loading={deleteLoading}
        onClose={() => {
          if (!deleteLoading) setDeleteTarget(null);
        }}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default MeetingNotesPage;
